import {
  callApi,
  getStoredValueWithCookieFallback,
} from "../../utils/common.utils";
import { format } from "date-fns/format";
import {
  PRIORITY_STORAGE_KEY,
  READY_FOR_MULCH_DESCRIPTION_STORAGE_KEY,
  STATE_VALIDATION_STORAGE_KEY,
} from "../../constants/app.constants";

export const formatDate = (date?: Date, formatStr = "MM/dd/yyyy") => {
  return date ? format(date, formatStr) : "";
};

export const fetchBenchmarkScheduledJobs = async (
  userId: string,
  startDate?: Date,
  endDate?: Date
) => {
  const newstartDate = formatDate(startDate);
  const newendDate = formatDate(endDate);
  const payLoad = {
    id: userId,
    startDate: newstartDate,
    endDate: newendDate,
  };
  return callApi("getScheduledJobs", payLoad, "getData");
};

export const fetchScheduledPropertyDetails = async (propertyId: string) => {
  const payLoad = {
    propertyid: propertyId,
  };
  return callApi("getDropdownPropertyDetails", payLoad, "getData");
};

export const fetchScheduledEditPropertyDetails = async (loadId: string) => {
  const payLoad = {
    loadid: loadId,
  };
  return callApi("getScheduledEditPropertyDetails", payLoad, "getData");
};

export const fetchBenchmarkSchedulingProperties = async (
  userId: string | null
) => {
  const payLoad = {
    id: userId,
    searchtext: "",
  };
  return callApi("getProperties", payLoad, "getData");
};

const getDescriptionValue = (descriptionResponse: any, fieldName: string) => {
  const descriptionData = Array.isArray(descriptionResponse)
    ? descriptionResponse[0]
    : descriptionResponse;

  if (typeof descriptionData === "string") {
    return descriptionData.trim();
  }

  return descriptionData?.[fieldName]?.trim() || "";
};

const getLoggedInCredentials = () => ({
  username: window.localStorage.getItem("username") || "",
  loggedid: getStoredValueWithCookieFallback("ID", [
    "ID"
  ]),
});

export const fetchReadyForMulchDescription = async () => {
  const cachedDescription = window.sessionStorage.getItem(
    READY_FOR_MULCH_DESCRIPTION_STORAGE_KEY
  );
    
  if (cachedDescription) {
    return cachedDescription;
  }

  window.sessionStorage.removeItem("readyForMulchDescription");

  const payLoad = {};
  const readyForMulchResponse = await callApi(
    "getScheduledReadyForMulch",
    payLoad,
    "getData"
  );
  const readyForMulchDescription = getDescriptionValue(
    readyForMulchResponse,
    "ReadyForMulch_Description"
  );

  if (readyForMulchDescription) {
    window.sessionStorage.setItem(
      READY_FOR_MULCH_DESCRIPTION_STORAGE_KEY,
      readyForMulchDescription
    );
  }

  return readyForMulchDescription;
};

export const fetchScheduledStateValidation = async () => {
  const cachedDescription = window.sessionStorage.getItem(
    STATE_VALIDATION_STORAGE_KEY
  );

  if (cachedDescription) {
    return cachedDescription;
  }

  const payLoad = {};
  const stateValidationResponse = await callApi(
    "getScheduledStateValidation",
    payLoad,
    "getData"
  );
  const stateValidation = getDescriptionValue(
    stateValidationResponse,
    "StateValidation"
  );

  if (stateValidation) {
    window.sessionStorage.setItem(
      STATE_VALIDATION_STORAGE_KEY,
      stateValidation
    );
  }

  return stateValidation;
};

export const fetchScheduledPriority = async () => {
  const cachedPriority = window.sessionStorage.getItem(PRIORITY_STORAGE_KEY);

  if (cachedPriority) {
    return cachedPriority;
  }

  window.sessionStorage.removeItem("Priority");

  const payLoad = {};
  const priorityResponse = await callApi(
    "getScheduledPriority",
    payLoad,
    "getData"
  );
  const priorityDescription = getDescriptionValue(
    priorityResponse,
    "Priority_Description"
  );

  if (priorityDescription) {
    window.sessionStorage.setItem(PRIORITY_STORAGE_KEY, priorityDescription);
  }

  return priorityDescription;
};

export const confirmReadyForMulchScheduled = async (
  scheduledId: number
) => {
  const loggedInCredentials = getLoggedInCredentials();
  const payLoad = {
    id: scheduledId,
    username: loggedInCredentials.username,
    loggedid: loggedInCredentials.loggedid,
  };
  return callApi("confirmReadyForMulchScheduled", payLoad, "updateData");
};

export const confirmPriorityScheduled = async (
  scheduledId: number
) => {
  const loggedInCredentials = getLoggedInCredentials();
  const payLoad = {
    id: scheduledId,
    username: loggedInCredentials.username,
    loggedid: loggedInCredentials.loggedid,
  };
  return callApi("confirmPriorityScheduled", payLoad, "updateData");
};

export const confirmHoldScheduled = async (scheduledId: number) => {
  const loggedInCredentials = getLoggedInCredentials();
  const payLoad = {
    id: scheduledId,
    username: loggedInCredentials.username,
    loggedid: loggedInCredentials.loggedid,
  };
  return callApi("confirmHoldScheduled", payLoad, "updateData");
};

export const moveFromListScheduled = async (scheduledId: number) => {
  const payLoad = {
    id: scheduledId,
  };
  return callApi("moveFromList", payLoad, "deleteData");
};

export const fetchBenchmarkCompletedJobs = async (
  userId: string,
  startDate?: Date,
  endDate?: Date
) => {
  const newstartDate = formatDate(startDate);
  const newendDate = formatDate(endDate);
  const payLoad = {
    id: userId,
    startDate: newstartDate,
    endDate: newendDate,
  };
  return callApi("getCompletedJobs", payLoad, "getData");
};

export const fetchBenchmarkSchedulingCustomerContactList = async (
  userId: string | null
) => {
  const payLoad = {
    id: userId,
  };
  return callApi("getCustomerContact", payLoad, "getData");
};


export const fetchApprovalScheduled = async (approveId: number) => {
  const payLoad = {
    id: approveId,
  };
  return callApi("getApprovalScheduled", payLoad, "getData");
};

export const fetchBenchmarkProducts = async () => {
  const payLoad = {};
  return callApi("getBenchmarkProducts", payLoad, "getData");
};

export const fetchProducts = async () => {
  const payLoad = {};
  return callApi("getProducts", payLoad, "getData");
};

export const updateSingleApprovalScheduled = async (
  approveId: number | null,
  estimatedYardage: string,
  notExceedYardage: string,
  overageApprovedBy: string,
  dateOverageApproved?: Date
) => {
  const newDateOverageApproved = formatDate(dateOverageApproved);
  const payLoad = {
    id: approveId,
    estimatedyardage: estimatedYardage,
    notexceedyardage: notExceedYardage,
    overageapprovedby: overageApprovedBy,
    dateoverageapproved: newDateOverageApproved,
  };
  return callApi("updateApprovalScheduled", payLoad, "updateData");
};
export const fetchRequestedChangeScheduled = async (userId: number) => {
  const payLoad = {
    id: userId,
  };
  return callApi("getRequestedChangeScheduled", payLoad, "getData");
};

export const updateSingleRequestedChangeScheduled = async (
  userId: number | null,
  requestedYardage: string,
  requestedProduct: string,
  note: string,
  requestedDeliveryDate?: Date,
  sensitiveClient?: number
) => {
  const newRequestedDeliveryDate = formatDate(requestedDeliveryDate);
  const newDateChangeRequested = formatDate(new Date());

  const payLoad = {
    id: userId,
    requesteddeliverydate: newRequestedDeliveryDate,
    requestedyardage: requestedYardage,
    requestedproduct: requestedProduct,
    note: note,
    datechangerequested: newDateChangeRequested,
    sensitiveclient: sensitiveClient,
  };
  return callApi("updateRequestedChangeScheduled", payLoad, "updateData");
};

export const fetchMapScheduled = async (userId: number) => {
  const payLoad = {
    id: userId,
  };
  return callApi("getMapScheduled", payLoad, "getData");
};

export const updateSingleMapScheduled = async (
  userId: number | null,
  companyRequestChange: string
) => {
  const payLoad = {
    id: userId,
    companyrequestchange: companyRequestChange,
  };
  return callApi("updateMapScheduled", payLoad, "updateData");
};

export const deleteMapImageScheduled = async (companyMap: string) => {
  const payLoad = {
    companymap: companyMap,
  };
  return callApi("deleteMapImage", payLoad, "getData");
};

export const addSchedulingProperty = async (
  orgId: string | null,
  propertyId: string,
  propertyName: string,
  address: string,
  contactFirstName: string,
  contactLastName: string,
  clientContactId: string,
  estimatedYards: string,
  productColor: string,
  productId: string,
  city: string,
  state: string,
  postalCode: string,
  propertyNotes: string,
  attachment?: File | null,
  isEditMode = false,
  loadId?: number
) => {
  const payLoad = {
    orgid: orgId,
    propertyid: propertyId,
    isedit: isEditMode ? 1 : 0,
    ...(isEditMode && loadId ? { loadid: loadId } : {}),
    propertyname: propertyName,
    address: address,
    contactfirstname: contactFirstName,
    contactlastname: contactLastName,
    clientcontactid: clientContactId,
    quantity: estimatedYards,
    productcolor: productColor,
    productId,
    city,
    state,
    postalcode: postalCode,
    propertynotes: propertyNotes,
  };

  return callApi("addNewProperty", payLoad, "addData", {
    attachment,
  });
};
